/**
 * Unified Catalog & Pack Substrate (Task #1945).
 *
 * Persistence layer for the cross-domain provisioning system that lets
 * Super Admin author declarative catalogs (under `server/data/catalogs/**`)
 * and apply / preview / rollback / diff them per tenant.
 *
 * All three tables are platform-scoped:
 *   - platform_catalog_versions     — what versions of each declarative
 *     catalog are currently synced into the platform from source.
 *   - tenant_catalog_applied_packs  — append-only audit ledger of pack
 *     apply / rollback events keyed by tenant.
 *   - tenant_catalog_overrides      — per-tenant deltas on top of catalog
 *     content so a tenant fork preserves catalog provenance.
 *
 * Source of truth for the substrate behaviour:
 *   - server/data/catalogs/_lib/define-catalog.ts (canonical hashing)
 *   - server/services/pack-service.ts             (apply/preview/diff/etc.)
 *   - server/bootstrap/sync-catalogs.ts           (sync at boot)
 *   - docs/source-of-truth/engineering/21_CATALOG_TAXONOMY.md
 */
import { sql } from "drizzle-orm";
import {
  pgTable,
  uuid,
  text,
  integer,
  boolean,
  timestamp,
  jsonb,
  index,
  unique,
  type AnyPgColumn,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import type { z } from "zod";

export const platformCatalogVersions = pgTable(
  "platform_catalog_versions",
  {
    id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
    domain: text("domain").notNull(),
    slug: text("slug").notNull(),
    version: integer("version").notNull(),
    contentHash: text("content_hash").notNull(),
    displayName: text("display_name").notNull(),
    description: text("description"),
    sourcePath: text("source_path").notNull(),
    syncedAt: timestamp("synced_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    isActive: boolean("is_active").notNull().default(true),
    metadata: jsonb("metadata")
      .$type<Record<string, unknown>>()
      .notNull()
      .default(sql`'{}'::jsonb`),
  },
  (t) => ({
    uniq: unique("platform_catalog_versions_uniq").on(t.domain, t.slug, t.version),
    activeIdx: index("platform_catalog_versions_active_idx").on(t.domain, t.slug),
  }),
);
export const insertPlatformCatalogVersionSchema = createInsertSchema(
  platformCatalogVersions,
).omit({ id: true, syncedAt: true });
export type InsertPlatformCatalogVersion = z.infer<typeof insertPlatformCatalogVersionSchema>;
export type PlatformCatalogVersion = typeof platformCatalogVersions.$inferSelect;

export const tenantCatalogAppliedPacks = pgTable(
  "tenant_catalog_applied_packs",
  {
    id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id").notNull(),
    packSlug: text("pack_slug").notNull(),
    /**
     * Catalog domain — added in migration 0137 because slugs are not globally
     * unique (e.g. `observability/dashboards-default` and
     * `reporting/dashboards-default`). Nullable for backward-compat with rows
     * written before the migration; the apply path always populates it now,
     * and the rollback resolver prefers (domain, slug, signature) when set.
     */
    packDomain: text("pack_domain"),
    packVersion: integer("pack_version").notNull(),
    packSignature: text("pack_signature").notNull(),
    decisions: jsonb("decisions")
      .$type<Array<Record<string, unknown>>>()
      .notNull()
      .default(sql`'[]'::jsonb`),
    result: jsonb("result")
      .$type<Record<string, unknown>>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    appliedBy: text("applied_by"),
    appliedAt: timestamp("applied_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    rollback: boolean("rollback").notNull().default(false),
    rollbackOfId: uuid("rollback_of_id").references(
      (): AnyPgColumn => tenantCatalogAppliedPacks.id,
      { onDelete: "set null" },
    ),
    source: text("source").notNull().default("pack-service"),
  },
  (t) => ({
    tenantIdx: index("tenant_catalog_applied_packs_tenant_idx").on(
      t.tenantId,
      t.appliedAt,
    ),
    packIdx: index("tenant_catalog_applied_packs_pack_idx").on(
      t.packSlug,
      t.packVersion,
    ),
    domainIdx: index("tenant_catalog_applied_packs_domain_idx").on(
      t.packDomain,
      t.packSlug,
      t.packVersion,
    ),
  }),
);
export const insertTenantCatalogAppliedPackSchema = createInsertSchema(
  tenantCatalogAppliedPacks,
).omit({ id: true, appliedAt: true });
export type InsertTenantCatalogAppliedPack = z.infer<
  typeof insertTenantCatalogAppliedPackSchema
>;
export type TenantCatalogAppliedPack = typeof tenantCatalogAppliedPacks.$inferSelect;

export const tenantCatalogOverrides = pgTable(
  "tenant_catalog_overrides",
  {
    id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id").notNull(),
    catalogSlug: text("catalog_slug").notNull(),
    catalogVersion: integer("catalog_version").notNull(),
    itemKey: text("item_key").notNull(),
    override: jsonb("override")
      .$type<Record<string, unknown>>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    createdBy: text("created_by"),
  },
  (t) => ({
    uniq: unique("tenant_catalog_overrides_uniq").on(
      t.tenantId,
      t.catalogSlug,
      t.itemKey,
    ),
    tenantIdx: index("tenant_catalog_overrides_tenant_idx").on(
      t.tenantId,
      t.catalogSlug,
    ),
  }),
);
export const insertTenantCatalogOverrideSchema = createInsertSchema(
  tenantCatalogOverrides,
).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertTenantCatalogOverride = z.infer<
  typeof insertTenantCatalogOverrideSchema
>;
export type TenantCatalogOverride = typeof tenantCatalogOverrides.$inferSelect;

/**
 * Task #1981 — Per-tenant materialised catalog items.
 *
 * Pack apply handlers write one row per installed catalog item here so a
 * subsequent rollback can deterministically remove what was installed.
 * `tenant_catalog_applied_packs` remains the audit ledger; this table is the
 * actual provisioned state surface that domain code can read from.
 */
export const tenantProvisionedItems = pgTable(
  "tenant_provisioned_items",
  {
    id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id").notNull(),
    catalogDomain: text("catalog_domain").notNull(),
    catalogSlug: text("catalog_slug").notNull(),
    catalogVersion: integer("catalog_version").notNull(),
    itemKey: text("item_key").notNull(),
    itemKind: text("item_kind").notNull(),
    displayName: text("display_name").notNull(),
    payload: jsonb("payload")
      .$type<Record<string, unknown>>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    appliedBy: text("applied_by"),
    appliedAt: timestamp("applied_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    uniq: unique("tenant_provisioned_items_uniq").on(
      t.tenantId,
      t.catalogDomain,
      t.catalogSlug,
      t.itemKey,
    ),
    tenantIdx: index("tenant_provisioned_items_tenant_idx").on(
      t.tenantId,
      t.catalogDomain,
    ),
    slugIdx: index("tenant_provisioned_items_slug_idx").on(
      t.tenantId,
      t.catalogSlug,
    ),
  }),
);
export const insertTenantProvisionedItemSchema = createInsertSchema(
  tenantProvisionedItems,
).omit({ id: true, appliedAt: true, updatedAt: true });
export type InsertTenantProvisionedItem = z.infer<
  typeof insertTenantProvisionedItemSchema
>;
export type TenantProvisionedItem = typeof tenantProvisionedItems.$inferSelect;
